import { FunctionComponent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";

import { ContextMenu, ContextMenuProps, MenuItem } from "@/components/ContextMenu/ContextMenu";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { NewTicketModal, NewTicketPrefill } from "./NewTicketModal";

interface ReportProblemMenuProps {
	/** The row's own entries; "Report a problem" is appended when the flag is on. */
	options: MenuItem[];
	prefill: NewTicketPrefill;
	width?: string;
	position?: ContextMenuProps["position"];
	/** The trigger (typically the three-dots icon). */
	children: ReactNode;
}

/**
 * A row's context menu with a flag-gated "Report a problem" entry appended, plus the ticket modal
 * it opens (PREC-3937). Each row owns its state, so a closed modal renders nothing at all and the
 * host menu needs no wiring beyond swapping `ContextMenu` for this component.
 */
export const ReportProblemMenu: FunctionComponent<ReportProblemMenuProps> = ({
	options,
	prefill,
	width = "150",
	position = "bottom-right",
	children,
}) => {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	// `undefined` means the feature flags have not loaded yet — the entry must stay hidden, and
	// with the flag off the tickets routes answer 403 anyway.
	const isSupportActive = useIsFeatureActive(FeaturesCodes.SUPPORT_TICKETS);

	const menuOptions: MenuItem[] = isSupportActive
		? [...options, { title: t("support.reportProblem"), onClick: () => setIsOpen(true) }]
		: options;

	return (
		<>
			<ContextMenu data={[menuOptions]} position={position} width={width}>
				{children}
			</ContextMenu>
			{isOpen && <NewTicketModal prefill={prefill} onClose={() => setIsOpen(false)} />}
		</>
	);
};
